import React from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  VStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiTrendingUp, FiStar, FiGlobe, FiTarget } from 'react-icons/fi';

import { buildGetMoreCustomers } from '../utils/getMoreCustomersBuilder';

const MotionBox = motion(Box);

const PLAY_META = {
  reviews: { icon: FiStar, color: 'green' },
  traffic: { icon: FiGlobe, color: 'blue' },
  ppc: { icon: FiTarget, color: 'purple' },
};

/**
 * CategoryBadge — compact pill combining a consistent-stroke icon with the
 * category label (Reviews / Website Traffic / Paid Ads). Restrained accent
 * tint instead of a full colored outline.
 */
const CategoryBadge = ({ icon, label, color }) => (
  <HStack
    as="span"
    display="inline-flex"
    align="center"
    spacing={1}
    h="20px"
    px="8px"
    borderRadius="full"
    bg={`${color}.50`}
    color={`${color}.700`}
    border="1px solid"
    borderColor={`${color}.100`}
    flexShrink={0}
  >
    <Flex
      as="span"
      align="center"
      justify="center"
      w="11px"
      h="11px"
      flex="0 0 11px"
    >
      <Icon as={icon} boxSize="11px" strokeWidth={2.25} display="block" />
    </Flex>
    <Text
      as="span"
      display="block"
      fontSize="10px"
      fontWeight="700"
      textTransform="uppercase"
      letterSpacing="0.06em"
      lineHeight="1"
      m={0}
    >
      {label}
    </Text>
  </HStack>
);

const PlayRow = ({ play, subtleText, headingColor, rowBg, rowBorder, rowHoverBg }) => {
  const meta = PLAY_META[play.key] || { icon: FiTrendingUp, color: 'green' };

  return (
    <Box
      p={2.5}
      borderRadius="12px"
      bg={rowBg}
      border="1px solid"
      borderColor={rowBorder}
      transition="background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease"
      _hover={{
        bg: rowHoverBg,
        borderColor: `${meta.color}.200`,
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06)',
      }}
    >
      <Box mb={1}>
        <CategoryBadge icon={meta.icon} label={play.category} color={meta.color} />
      </Box>

      <Text
        fontSize="13px"
        fontWeight="700"
        color={headingColor}
        lineHeight="1.35"
        letterSpacing="-0.005em"
      >
        {play.action}
      </Text>

      {play.body && (
        <Text fontSize="12px" color={subtleText} lineHeight="1.5" mt={1}>
          {play.body}
        </Text>
      )}
    </Box>
  );
};

/**
 * GetMoreCustomersCard
 *
 * The left-hand ("Get More Customers") panel of the acquisition/retention
 * row below the Localytics Score breakdown. Renders exactly 3 deterministic
 * acquisition plays:
 *   1. Reviews / map pack (velocity vs. #1 competitor)
 *   2. Website traffic (traffic vs. #1 competitor)
 *   3. PPC ads (start, or copy competitors)
 */
const GetMoreCustomersCard = ({ reviews, traffic, ads, dataTestId }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.900', 'white');
  const dividerColor = useColorModeValue('gray.100', 'gray.700');
  const rowBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const rowBorder = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHoverBg = useColorModeValue('white', 'whiteAlpha.100');
  const tileBg = useColorModeValue('green.50', 'green.900');
  const tileBorder = useColorModeValue('green.100', 'green.800');
  const tileIcon = useColorModeValue('green.600', 'green.300');
  const cardShadow = useColorModeValue(
    '0 1px 3px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.04)',
    '0 1px 3px rgba(0, 0, 0, 0.4)'
  );

  const plays = buildGetMoreCustomers({ reviews, traffic, ads });

  return (
    <MotionBox
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      bg={cardBg}
      borderRadius="16px"
      p={{ base: 4, md: 5 }}
      border="1px solid"
      borderColor={cardBorder}
      boxShadow={cardShadow}
      position="relative"
      h="100%"
      data-testid={dataTestId || 'get-more-customers-card'}
    >
      <Flex align="center" gap={2.5} pb={3} mb={3} borderBottom="1px solid" borderColor={dividerColor}>
        <Flex
          w="32px"
          h="32px"
          align="center"
          justify="center"
          borderRadius="10px"
          bg={tileBg}
          color={tileIcon}
          border="1px solid"
          borderColor={tileBorder}
          flexShrink={0}
        >
          <Icon as={FiTrendingUp} boxSize="16px" strokeWidth={2.25} />
        </Flex>
        <Text
          fontSize={{ base: 'lg', md: 'xl' }}
          fontWeight="700"
          color={headingColor}
          letterSpacing="-0.01em"
          lineHeight="1.2"
        >
          Get More Customers
        </Text>
      </Flex>

      <VStack align="stretch" spacing={2}>
        {plays.map((play, idx) => (
          <PlayRow
            key={play.key || idx}
            play={play}
            subtleText={subtleText}
            headingColor={headingColor}
            rowBg={rowBg}
            rowBorder={rowBorder}
            rowHoverBg={rowHoverBg}
          />
        ))}
      </VStack>
    </MotionBox>
  );
};

export default GetMoreCustomersCard;
